// Reading the yard's workbooks for the BOM lists page, in the browser: the STD lists workbook (one "<MODEL>
// BOXES" sheet per model; hidden sheets are old versions and are skipped) and the standard-equipment list.
// The reading itself is packages/shared domain/bom.ts, so it is tested there.
import { bomSheetModel, parseBomSheet, parseStdEquipment, type BomSection } from '@technohull/shared';

/* eslint-disable @typescript-eslint/no-explicit-any */

export interface BomSheetRead {
  /** the sheet's name, with its "UPD" date when it has one ("GT7 BOXES · UPD 8/10/26") */
  sheet: string;
  /** the sheet's name in the workbook ("GT7 BOXES") */
  tab: string;
  model: string;
  sections: BomSection[];
  warnings: string[];
}

async function readBook(file: File): Promise<{ XLSX: any; wb: any }> {
  const mod: any = await import('xlsx');
  const XLSX = mod.default ?? mod;
  const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
  return { XLSX, wb };
}

// every row of the sheet's range, blank ones too, so a row's place in the list is its place on the sheet
const rowsOf = (XLSX: any, ws: any): unknown[][] => XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: null, blankrows: true });
/** the sheet row (Excel's numbering) of the first row rowsOf gives */
const firstRowOf = (XLSX: any, ws: any): number => (ws?.['!ref'] ? XLSX.utils.decode_range(ws['!ref']).s.r + 1 : 1);

/** each model's sections and parts from the STD lists workbook, one per model (its first visible sheet) */
export async function readStdLists(file: File): Promise<BomSheetRead[]> {
  const { XLSX, wb } = await readBook(file);
  const out: BomSheetRead[] = [];
  (wb.SheetNames as string[]).forEach((name, i) => {
    if (wb.Workbook?.Sheets?.[i]?.Hidden) return;
    const model = bomSheetModel(name);
    if (!model || out.some((o) => o.model === model)) return;
    const ws = wb.Sheets[name];
    const { sections, warnings, updated } = parseBomSheet(rowsOf(XLSX, ws), firstRowOf(XLSX, ws));
    // the sheet's own "UPD" date rides on its name, so the list says how current it is
    out.push({ sheet: updated ? `${name} · UPD ${updated}` : name, tab: name, model, sections, warnings });
  });
  return out;
}

/** each model's standard items, and the order rows they tick, from the standard-equipment list */
export async function readStdEquipment(file: File): Promise<Array<{ model: string; items: string[]; fields: string[] }>> {
  const { XLSX, wb } = await readBook(file);
  const found = new Map<string, { model: string; items: string[]; fields: string[] }>();
  for (const name of wb.SheetNames as string[]) {
    for (const m of parseStdEquipment(rowsOf(XLSX, wb.Sheets[name]))) if (!found.has(m.model)) found.set(m.model, m);
  }
  return [...found.values()];
}
